import {
    type FC,
    type MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { history } from "#/_Bootstrap/RoutingAndStore.ts";
import { showDialog } from "#/Actions/Dialogs/DialogActions.ts";
import {
    DEFAULT_BORDER_SETTINGS,
    DEFAULT_PADDING_SETTINGS,
    type DragDropDefaultOverride,
    DragDropEditor,
} from "#/Common/Forms/DragDrop/DragDropEditor.tsx";
import { DragDropManager } from "#/Common/Forms/DragDrop/DragDropManager.ts";
import type { ComponentType, DragDropEditItemMap } from "#/Common/Forms/DragDrop/DragDropTypes.ts";
import {
    DragDropImageWidthChecker,
} from "#/Common/Forms/DragDrop/InternalComponents/Image/DragDropImageWidthChecker.tsx";
import type { ColumnType } from "#/Common/Forms/DragDrop/ReorderableTableSettings.tsx";
import LoadingScreen from "#/Common/Loading/LoadingScreen.tsx";
import PopupManager from "#/Common/Popups/PopupManager.ts";
import { ReceiptAccountTableComponent } from "#/Common/Receipts/Components/ReceiptAccountTableComponent.tsx";
import { ReceiptBarcodeComponent } from "#/Common/Receipts/Components/ReceiptBarcodeComponent.tsx";
import { ReceiptEFTPOSComponent } from "#/Common/Receipts/Components/ReceiptEFTPOSComponent.tsx";
import { ReceiptGiftCardsComponent } from "#/Common/Receipts/Components/ReceiptGiftCardsComponent.tsx";
import { ReceiptHeadingComponent } from "#/Common/Receipts/Components/ReceiptHeadingComponent.tsx";
import { ReceiptInvoiceTableComponent } from "#/Common/Receipts/Components/ReceiptInvoiceTableComponent.tsx";
import { ReceiptLogoComponent } from "#/Common/Receipts/Components/ReceiptLogoComponent.tsx";
import { ReceiptLoyaltyTableComponent } from "#/Common/Receipts/Components/ReceiptLoyaltyTableComponent.tsx";
import { ReceiptPaymentTableComponent } from "#/Common/Receipts/Components/ReceiptPaymentTableComponent.tsx";
import { ReceiptProductTableComponent } from "#/Common/Receipts/Components/ReceiptProductTableComponent.tsx";
import { ReceiptSurchargeComponent } from "#/Common/Receipts/Components/ReceiptSurchargeComponent.tsx";
import {
    isTaxIndicatorComponent,
    ReceiptTaxIndicatorComponent,
} from "#/Common/Receipts/Components/ReceiptTaxIndicatorComponent.tsx";
import { ReceiptTaxTableComponent } from "#/Common/Receipts/Components/ReceiptTaxTableComponent.tsx";
import { ReceiptTotalTableComponent } from "#/Common/Receipts/Components/ReceiptTotalTableComponent.tsx";
import { ReceiptAccountTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptAccountTableSettings.tsx";
import { ReceiptGiftCardsSettings } from "#/Common/Receipts/Components/Settings/ReceiptGiftCardsSettings.tsx";
import { ReceiptHeadingSettings } from "#/Common/Receipts/Components/Settings/ReceiptHeadingSettings.tsx";
import { ReceiptInvoiceTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptInvoiceTableSettings.tsx";
import { ReceiptLogoSettings } from "#/Common/Receipts/Components/Settings/ReceiptLogoSettings.tsx";
import { ReceiptLoyaltyTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptLoyaltyTableSettings.tsx";
import { ReceiptPaymentTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptPaymentTableSettings.tsx";
import { ReceiptProductTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptProductTableSettings.tsx";
import {
    ReceiptSurchargeTableSettings,
} from "#/Common/Receipts/Components/Settings/ReceiptSurchargeTableSettings.tsx";
import { ReceiptTaxIndicatorSettings } from "#/Common/Receipts/Components/Settings/ReceiptTaxIndicatorSettings.tsx";
import { ReceiptTaxTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptTaxTableSettings.tsx";
import { ReceiptTotalTableSettings } from "#/Common/Receipts/Components/Settings/ReceiptTotalTableSettings.tsx";
import { ReceiptAccountContext, ReceiptTypeContext } from "#/Common/Receipts/Receipt.tsx";
import type {
    ReceiptPaddingType,
    ReceiptServerTypeUnion,
} from "#/Data/_Bases/BaseReceiptRepository.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import {
    caseText,
    formatDate,
    formatDateTime,
    pluralize,
    singleText,
    uppercaseFirst,
} from "#/Utilities/Generic/Strings.ts";
import FontAwesomeSolid from "#/Utilities/Platform/FontAwesome/FontAwesomeSolid.tsx";
import { useHelp } from "#/Utilities/Platform/HelpManager/HelpManager.tsx";
import { getOption } from "#/Utilities/Platform/Options/Options.ts";
import Request from "#/Utilities/Platform/Request.ts";
import { Subscription } from "#/Utilities/Platform/Subscription/Subscription.tsx";
import AgentManager from "#/Workers/Bases/AgentManager.ts";
import SyncAgent from "#/Workers/Sync/SyncAgent.tsx";
import { AGENT_EVENTS } from "#/Workers/Sync/SyncEvents.ts";
import type { LocalDatabaseOutlet, LocalDatabaseReceipt } from "../../../_Generated/Sync.gen";
import { ReceiptConfigure } from "./ReceiptConfigure.tsx";
import { ReceiptModifyWrapper } from "./ReceiptModifyWrapper.tsx";
import ReceiptsHelp from "./ReceiptsHelp.tsx";

const DUMMY_CUSTOMER = {
    code          : "12345",
    company       : "Shopfront",
    name          : "Keith Smith",
    firstName     : "Keith",
    lastName      : "Smith",
    businessNumber: "0123456789",
    birthday      : "1970-01-01",
    gender        : "",
    emails        : "hello@shopfront.com.au",
    fax           : "03-9330-3335",
    phone         : "03-9330-3335",
    mobile        : "03-9330-3335",
    website       : "shopfront.com.au",
    twitter       : "@shopfront",
    facebook      : "shopfront",
    billing       : {
        addressStreet1 : "425 Smith Street",
        addressStreet2 : "",
        addressSuburb  : "Fitzroy",
        addressPostcode: "3065",
        addressCountry : "Australia",
        addressState   : "Victoria",
        addressRegion  : "Fitzroy",
    },
    delivery: {
        addressStreet1 : "425 Smith Street",
        addressStreet2 : "",
        addressSuburb  : "Fitzroy",
        addressPostcode: "3065",
        addressCountry : "Australia",
        addressState   : "Victoria",
        addressRegion  : "Fitzroy",
    },
    invoiceMessage: "Customer invoice message",
    comments      : "Customer comments",
    customerGroup : {
        name: "Shopfront Customer Group",
    },
    loyaltyEnabled   : false,
    loyaltyRate      : 0,
    loyaltyPoints    : 0,
    disablePromotions: false,
    priceList        : {
        name: "Default Price List",
    },
    accountLimit: 999,
    currentOwing: 0,
};

export function getReceiptDefaultColumns(
    component: string,
    type: ReceiptServerTypeUnion,
    account: boolean
): Array<ColumnType> {
    switch(component) {
        case "product":
            if(type === "TEXT_ONLY") {
                return [{
                    column : "product",
                    name   : "Item",
                    enabled: true,
                }, {
                    column : "qty",
                    name   : "QTY",
                    enabled: true,
                }, {
                    column : "price",
                    name   : "Price",
                    enabled: true,
                }];
            }

            return [{
                column : "product",
                name   : "Item",
                enabled: true,
            }, {
                column : "case-qty",
                name   : "Case QTY",
                enabled: true,
            }, {
                column : "qty",
                name   : "QTY",
                enabled: true,
            }, {
                column : "normal-price",
                name   : "Normal Price",
                enabled: false,
            }, {
                column : "item-price",
                name   : `${uppercaseFirst(singleText())} Price`,
                enabled: true,
            }, {
                column : "case-price",
                name   : `${uppercaseFirst(caseText())} Price`,
                enabled: true,
            }, {
                column : "total-items",
                name   : `Total ${uppercaseFirst(pluralize(singleText(), 2))}`,
                enabled: false,
            }, {
                column : "discount",
                name   : "Discount",
                enabled: true,
            }, {
                column : "price",
                name   : "Price",
                enabled: true,
            }];
        case "tax":
            return [{
                column : "tax",
                name   : "Tax",
                enabled: true,
            }, {
                column : "total",
                name   : "Total",
                enabled: true,
            }];
        case "payment":
            return [{
                column : "payment",
                name   : "Payments",
                enabled: true,
            }, {
                column : "amount",
                name   : "Amount",
                enabled: true,
            }];
        case "loyalty":
            return [{
                column : "current-loyalty",
                name   : "Current Points",
                enabled: true,
            }, {
                column : "earned",
                name   : "Earned",
                enabled: true,
            }, {
                column : "spent",
                name   : "Spent",
                enabled: true,
            }, {
                column : "before-sale",
                name   : "Before Sale",
                enabled: true,
            }, {
                column : "after-sale",
                name   : "After Sale",
                enabled: true,
            }];
        case "account":
            return [{
                column : "start-balance",
                name   : "Start Balance",
                enabled: true,
            }, {
                column : "end-balance",
                name   : "End Balance",
                enabled: true,
            }, {
                column : "changed-balance",
                name   : "Balance Changed",
                enabled: true,
            }, {
                column : "current-balance",
                name   : "Current Balance",
                enabled: true,
            }];
        case "total":
            if(account) {
                return [{
                    column : "total",
                    name   : "Total",
                    enabled: true,
                }, {
                    column : "invoiceCount",
                    name   : "Invoices",
                    enabled: true,
                }, {
                    column : "outstanding",
                    name   : "Outstanding",
                    enabled: true,
                }, {
                    column : "change",
                    name   : "Change",
                    enabled: true,
                }, {
                    column : "rounding",
                    name   : "Rounding",
                    enabled: true,
                }];
            }

            return [{
                column : "rounding",
                name   : "Rounding",
                enabled: true,
            }, {
                column : "savings",
                name   : "Savings",
                enabled: true,
            }, {
                column : "discount",
                name   : "Discount",
                enabled: true,
            }, {
                column : "sale",
                name   : "Total",
                enabled: true,
            }, {
                column : "change",
                name   : "Change",
                enabled: true,
            }];
        case "invoices":
            return [{
                column : "timestamp",
                name   : "Processed Time",
                enabled: true,
            }, {
                column : "invoice-id",
                name   : "Invoice Id",
                enabled: true,
            }, {
                column : "allocated",
                name   : "Allocated",
                enabled: true,
            }, {
                column : "remaining",
                name   : "Remaining",
                enabled: false,
            }, {
                column : "total",
                name   : "Invoice Total",
                enabled: true,
            }];
        case "gift_cards":
            return [{
                column : "original",
                name   : "Original",
                enabled: true,
            }, {
                column : "amount",
                name   : "Amount Used",
                enabled: true,
            }, {
                column : "current",
                name   : "Current",
                enabled: true,
            }, {
                column : "expiry",
                name   : "Expiry",
                enabled: false,
            }];
        case "surcharge":
            return [{
                column : "name",
                name   : "Surcharge",
                enabled: true,
            }, {
                column : "amount",
                name   : "Amount",
                enabled: true,
            }];
    }

    return [];
}

function generateInvoiceNo(): string {
    const zeros = Math.round(Math.random() * 4) + 3;

    const suffix = Math.round(Math.random() * 9999);

    return "0".repeat(zeros) + suffix;
}

const ReceiptModify: FC = () => {
    const dispatch = useShopfrontDispatch();

    const outlet = useShopfrontSelector(state => state.location.outlet?.name ?? "Global Outlet");
    const outletId = useShopfrontSelector(state => state.location.outlet?.id ?? null);
    const register = useShopfrontSelector(state => state.location.register?.name ?? "Global Register");
    const user = useShopfrontSelector(state => state.user.id ? state.user.name : "");

    const { id } = useParams();

    const [ loading, setLoading ] = useState(true);
    const [ taxRateMap, setTaxRateMap ] = useState<Record<string, string>>({});
    const [ taxIndicators, setTaxIndicators ] = useState<Record<string, string>>({});
    const [ hasSurcharge, setHasSurcharge ] = useState(false);

    const [ localOutlet, setLocalOutlet ] = useState<null | LocalDatabaseOutlet>(null);
    const [ account, setAccount ] = useState(false);
    const [ type, setType ] = useState<ReceiptServerTypeUnion>("A4");
    const [ name, setName ] = useState("");
    const [ manager, setManager ] = useState<null | DragDropManager>(null);
    const [ receiptWidth, setReceiptWidth ] = useState<number | null>(null);
    const [ padding, setPadding ] = useState<ReceiptPaddingType | null>(null);

    const invoiceNo = useMemo(() => {
        return generateInvoiceNo();
    }, []);

    // Load the tax rates
    useEffect(() => {
        determineDataSource()
            .then(source => source.taxRates.all())
            .then(taxRates => {
                const map: Record<string, string> = {
                    "": "No Tax",
                };

                for(const taxRate of taxRates) {
                    map[taxRate.uuid] = taxRate.name;
                }

                setTaxRateMap(map);
            });
    }, []);

    useEffect(() => {
        if(outletId === null) {
            return;
        }

        determineDataSource()
            .then(source => source.outlets.get(outletId))
            .then(setLocalOutlet);
    }, [ outletId ]);

    const onLoad = useCallback(() => {
        if(!id) {
            return;
        }

        setLoading(true);

        Request.post<{
            errors?: unknown;
            data: {
                receipt: {
                    name: string;
                    type: ReceiptServerTypeUnion;
                    components: Array<ComponentType>;
                    accountReceipt: boolean;
                    receiptWidth: number | null;
                    padding: ReceiptPaddingType | null;
                };
            };
        }>("/graphql", {
            query: `query GetReceipt($id: ID!) {
                receipt(id: $id) {
                    name,
                    type,
                    components,
                    accountReceipt,
                    receiptWidth,
                    padding {
                        top,
                        left,
                        right,
                        bottom
                    },
                }
            }`,
            variables: {
                id,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                const receipt = response.data.data.receipt;

                setType(receipt.type);
                setName(receipt.name);
                setAccount(receipt.accountReceipt);
                setManager(new DragDropManager(receipt.components));
                setPadding(receipt.padding);
                setReceiptWidth(receipt.receiptWidth);
                setLoading(false);
            })
            .catch(err => {
                console.error("We could not fetch the receipt", err);
            });
    }, [ id ]);

    useEffect(() => {
        onLoad();
    }, [ onLoad ]);

    const override = useMemo((): DragDropDefaultOverride => {
        if(type === "TEXT_ONLY") {
            return {
                image: {
                    disabled: true,
                },
            };
        } else if(type === "EMAIL") {
            return {
                image: {
                    settings: DragDropImageWidthChecker,
                },
            };
        }

        return {};
    }, [ type ]);

    const items = useMemo<DragDropEditItemMap>(() => {
        const results: DragDropEditItemMap = {};

        if(!account) {
            results.heading = {
                component: ReceiptHeadingComponent,
                settings : ReceiptHeadingSettings,
                onCreate : () => ({
                    value: {
                        completed: "Tax Invoice",
                        parked   : "Held Sale",
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        textAlign : "left",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Header",
                icon  : "heading",
                styles: [ "color", "background", "fontSize", "fontWeight", "textAlign", "padding", "border" ],
            };
        }

        if(type !== "TEXT_ONLY") {
            results.barcode = {
                component: ReceiptBarcodeComponent,
                onCreate : () => ({
                    value: "",
                    style: { padding: DEFAULT_PADDING_SETTINGS },
                }),
                settings: () => null,
                name    : "Barcode",
                icon    : "barcode-alt",
                styles  : [ "padding" ],
            };

            results.logo = {
                component: ReceiptLogoComponent,
                onCreate : () => ({
                    value: {
                        fallback: null,
                    },
                    style: {
                        width     : "auto",
                        height    : "auto",
                        horizAlign: "center",
                        background: "#FFFFFF",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                settings: ReceiptLogoSettings,
                name    : "Outlet Logo",
                icon    : "image-polaroid",
                styles  : [ "width", "height", "horizAlign", "background", "padding", "border" ],
            };
        }

        if(account) {
            results.invoices = {
                component: ReceiptInvoiceTableComponent,
                settings : ReceiptInvoiceTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("invoices", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Invoices",
                icon  : "file-invoice-dollar",
                styles: [ "color", "background", "fontSize", "padding", "border" ],
            };

            results.total = {
                component: ReceiptTotalTableComponent,
                settings : ReceiptTotalTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("total", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Totals",
                icon  : "dollar-sign",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results.payment = {
                component: ReceiptPaymentTableComponent,
                settings : ReceiptPaymentTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("payment", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Payments",
                icon  : "credit-card",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };
        } else {
            results.product = {
                component: ReceiptProductTableComponent,
                settings : ReceiptProductTableSettings,
                onCreate : () => ({
                    value: {
                        showProductNote   : true,
                        showDiscountReason: false,
                        columns           : getReceiptDefaultColumns("product", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Products",
                icon  : "shopping-basket",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results.tax = {
                component: ReceiptTaxTableComponent,
                settings : ReceiptTaxTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("tax", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Tax",
                icon  : "percent",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results["tax_indicator"] = {
                component: ReceiptTaxIndicatorComponent,
                settings : ReceiptTaxIndicatorSettings,
                onCreate : () => ({
                    value: { indicator: "*", text: "Indicates items with", tax: "" },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Tax Indicator",
                icon  : "asterisk",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results.payment = {
                component: ReceiptPaymentTableComponent,
                settings : ReceiptPaymentTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("payment", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Payments",
                icon  : "credit-card",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            if(Subscription.can(Subscription.Features.HOLIDAY_SURCHARGING)) {
                results.surcharge = {
                    component: ReceiptSurchargeComponent,
                    settings : ReceiptSurchargeTableSettings,
                    onCreate : () => ({
                        value: {
                            columns: getReceiptDefaultColumns("surcharge", type, account),
                        },
                        style: {
                            color     : "#000000",
                            background: "#FFFFFF",
                            fontSize  : "12",
                            fontWeight: "normal",
                            padding   : DEFAULT_PADDING_SETTINGS,
                            border    : DEFAULT_BORDER_SETTINGS,
                        },
                    }),
                    name  : "Surcharge",
                    icon  : "badge-dollar",
                    styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
                };
            }

            results.loyalty = {
                component: ReceiptLoyaltyTableComponent,
                settings : ReceiptLoyaltyTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("loyalty", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Loyalty",
                icon  : "trophy",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results.account = {
                component: ReceiptAccountTableComponent,
                settings : ReceiptAccountTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("account", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Account",
                icon  : "user",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results.total = {
                component: ReceiptTotalTableComponent,
                settings : ReceiptTotalTableSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("total", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Totals",
                icon  : "dollar-sign",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };

            results["gift_cards"] = {
                component: ReceiptGiftCardsComponent,
                settings : ReceiptGiftCardsSettings,
                onCreate : () => ({
                    value: {
                        columns: getReceiptDefaultColumns("gift_cards", type, account),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                        padding   : DEFAULT_PADDING_SETTINGS,
                        border    : DEFAULT_BORDER_SETTINGS,
                    },
                }),
                name  : "Gift Cards",
                icon  : "gift-card",
                styles: [ "color", "background", "fontSize", "fontWeight", "padding", "border" ],
            };
        }

        results.eftpos = {
            component: ReceiptEFTPOSComponent,
            onCreate : () => ({
                value: null,
                style: {
                    textAlign: "left",
                    padding  : DEFAULT_PADDING_SETTINGS,
                    border   : DEFAULT_BORDER_SETTINGS,
                },
            }),
            settings: () => null,
            name    : "External Receipt",
            icon    : "file-alt",
            styles  : [ "textAlign", "padding", "border" ],
        };

        return results;
    }, [ account, type ]);

    useHelp(ReceiptsHelp);

    useEffect(() => {
        if(!manager) {
            return;
        }

        const listener = () => {
            const components = manager.getComponents("tax_indicator");

            const results: Record<string, string> = {};

            for(const component of components) {
                if(!isTaxIndicatorComponent(component)) {
                    continue;
                }

                results[component.value.tax] = component.value.indicator;
            }

            setTaxIndicators(results);
            setHasSurcharge(manager.hasComponentType("surcharge"));
        };

        listener();

        manager.addListener("update", listener);

        return () => {
            manager.removeListener("update", listener);
        };
    }, [ manager ]);

    const data = useMemo(() => {
        const taxRates = [ ...new Set([ "", ...Object.keys(taxIndicators) ]) ];

        const products = taxRates.map((taxRate, index) => ({
            "product"        : `Test ${(taxRateMap[taxRate] || "Product")}`,
            "case-qty"       : "24",
            "qty"            : "2",
            "total-items"    : "48",
            "item-price"     : "$9.99",
            "case-price"     : "$239.76",
            "discount"       : "$1.00",
            "price"          : "$19.98",
            "tax"            : taxRate,
            "discount-reason": "Test discount reason",
            "note"           : "Test product note",
            "components"     : [] as Array<{ type: string; name: string; price: number; }>,
            "normal-price"   : "$20.98",
            "savings"        : "$1.00",
            "surcharge"      : hasSurcharge && index === 0 ? "1" : "",
        }));

        if(getOption("use_components_and_packages")) {
            products.push({
                "product"        : "Test Package",
                "case-qty"       : "24",
                "qty"            : "2",
                "total-items"    : "48",
                "item-price"     : "$9.99",
                "case-price"     : "$239.76",
                "discount"       : "$1.00",
                "price"          : "$19.98",
                "tax"            : taxRates[taxRates.length - 1],
                "discount-reason": "Test component discount reason",
                "note"           : "Test component note",
                "components"     : [{
                    type : "deleted",
                    name : "Removed Component",
                    price: -0.5,
                }, {
                    type : "added",
                    name : "Added Component",
                    price: 1.5,
                }],
                "normal-price": "$20.98",
                "savings"     : "$1.00",
                "surcharge"   : "",
            });
        }

        let total;

        if(account) {
            total = {
                total       : 39.99,
                invoiceCount: 2,
                outstanding : 15,
                change      : 10,
                rounding    : 0.01,
            };
        } else {
            const productCount = products.length;

            total = {
                sale     : 19.98 * productCount,
                remaining: 0.01,
                savings  : productCount,
                discount : productCount,
                change   : 5,
                rounding : 0.01 * productCount,
            };
        }

        return {
            status    : "completed",
            indicators: taxIndicators,
            logo      : localOutlet?.logo ?? null,
            tax       : taxRates.map(taxRate => ({
                id   : taxRate,
                tax  : taxRateMap[taxRate] || "Test Tax",
                total: taxRate ? "$0.50" : "$0.00",
            })),
            payments: [
                { "payment": "Cash", "amount": "$5.00" },
                { "payment": "EFTPOS", "amount": "$20.00" },
            ],
            loyalty: {
                "earned"         : "0000",
                "spent"          : "0000",
                "before-sale"    : "0000",
                "after-sale"     : "0000",
                "current-loyalty": "0000",
            },
            surcharges: hasSurcharge ? [{
                indicator: "1",
                name     : "Surcharge",
                amount   : "$2.00",
            }] : [],
            account: {
                "start-balance"  : 80,
                "end-balance"    : 100,
                "changed-balance": 20,
            },
            invoices: [{
                "timestamp" : formatDateTime("2018-02-17 12:00:05"),
                "invoice-id": "12",
                "allocated" : "$12.00",
                "remaining" : "$13.00",
                "total"     : "$25.00",
            }, {
                "timestamp" : formatDateTime("2018-02-18 11:56:42"),
                "invoice-id": "13",
                "allocated" : "$14.99",
                "remaining" : "$0.00",
                "total"     : "$14.99",
            }],
            eftpos: `------------------------
EFTPOS FROM BANK

INGENICO DEMO TERMINAL
THE APOLLO 9 JUGGERNAUT
INGENICO DEMO TERMINAL
MID: DEFINED
TID: 44444444 ROC:123456
========================
560254  230(S)
CHEQUE A/C
DEBIT CARD         04 18
JUL 15,15          03:15

SALE
APPROVED              00

AMOUNT     AUD     $5.00
---------
TOTAL      AUD     $5.00

------------------------`,
            text: {
                invoiceNo,
                completedAt     : moment(),
                currentTimestamp: moment(),
                user            : {
                    name: user,
                },
                outlet: {
                    name          : outlet,
                    businessNumber: localOutlet?.business_number ?? "",
                    contact       : {
                        addressStreet1 : localOutlet?.contact?.address_street_1 ?? "",
                        addressStreet2 : localOutlet?.contact?.address_street_2 ?? "",
                        addressSuburb  : localOutlet?.contact?.address_suburb ?? "",
                        addressCity    : localOutlet?.contact?.address_city ?? "",
                        addressPostcode: localOutlet?.contact?.address_postcode ?? "",
                        addressRegion  : localOutlet?.contact?.address_suburb ?? "",
                        addressCountry : localOutlet?.contact?.address_country ?? "",
                        addressState   : localOutlet?.contact?.address_state ?? "",
                        email          : localOutlet?.contact?.email ?? "",
                        facebook       : localOutlet?.contact?.facebook ?? "",
                        fax            : localOutlet?.contact?.fax ?? "",
                        mobile         : localOutlet?.contact?.mobile ?? "",
                        phone          : localOutlet?.contact?.phone ?? "",
                        twitter        : localOutlet?.contact?.twitter ?? "",
                        website        : localOutlet?.contact?.website ?? "",
                    },
                },
                register: {
                    name: register,
                },
                customer: DUMMY_CUSTOMER,
                note    : "Example Note",
            },
            giftCards: [{
                code    : "12345",
                current : 100,
                original: 120,
                amount  : 20,
                expiry  : formatDate("2018-02-17"),
            }, {
                code    : "54321",
                current : 80,
                original: 90,
                amount  : 10,
                expiry  : formatDate("2018-02-18"),
            }],
            invoiceNo,
            products,
            total,
        };
    }, [ hasSurcharge, taxIndicators, account, invoiceNo, user, outlet, register, taxRateMap, localOutlet ]);

    const onConfigureConfirm = ({
        receiptWidth,
        padding,
    }: {
        receiptWidth: number | null;
        padding: null | ReceiptPaddingType;
    }): void => {
        setReceiptWidth(receiptWidth ?? 0);
        setPadding(padding);
    };

    const onConfigureClick = (e: MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();

        dispatch(showDialog(
            "Configure Receipt",
            <ReceiptConfigure
                id={id as string}
                onConfirm={onConfigureConfirm}
            />,
            "information"
        ));
    };

    const onCancel = () => {
        history.push("/settings/receipts");
    };

    const onSave = (quit = false): void => {
        if(!manager || !id) {
            return;
        }

        if(!name.trim()) {
            PopupManager.error("Please enter a valid name");

            return;
        }

        const components = manager.toArray();

        Request.post("/graphql", {
            query: `mutation UpdateReceipt(
                $id: ID!, 
                $name: String, 
                $components: JSON, 
                $receiptWidth: Float,
                $padding: ReceiptPaddingInput
            ) {
                updateReceipt(
                    id: $id, 
                    name: $name, 
                    components: $components, 
                    receiptWidth: $receiptWidth,
                    padding: $padding
                ) {
                    id,
                }
            }`,
            variables: {
                id,
                name,
                receiptWidth,
                components,
                padding,
            },
        })
            .then(async () => {
                try {
                    const source = await determineDataSource();

                    const update: Partial<LocalDatabaseReceipt> = {
                        components: JSON.stringify(components),
                        name,
                    };

                    if(typeof receiptWidth === "number") {
                        update.receiptWidth = receiptWidth;
                    }

                    await source.receipts.update(id, update);
                } catch(e) {
                    // We couldn't update the receipt, force the sync to try and catch up
                    AgentManager.getAgent(SyncAgent).sendMessage(AGENT_EVENTS.SYNC_PERFORM);
                }

                PopupManager.success("Successfully saved Receipt");

                if(quit) {
                    onCancel();
                } else {
                    onLoad();
                }
            })
            .catch(err => {
                PopupManager.error("Error whilst trying to update the receipt");
                console.error("Unable to update the receipt", err);
            });
    };

    if(loading || !manager) {
        return (
            <LoadingScreen>
                Loading Receipt...
            </LoadingScreen>
        );
    }

    return (
        <ReceiptTypeContext.Provider value={type}>
            <ReceiptAccountContext.Provider value={account}>
                <DragDropEditor
                    items={items}
                    wrapper={ReceiptModifyWrapper}
                    manager={manager}
                    override={override}
                    data={data}
                    onSave={onSave}
                    onCancel={onCancel}
                    name={name}
                    title={(
                        <>
                            <div className="text-2xl whitespace-nowrap">Receipt -</div>
                            <input
                                type="text"
                                name="name"
                                className="mx-2 p-2"
                                onChange={(e) => {
                                    setName(e.currentTarget.value);
                                }}
                                value={name}
                            />
                            <button
                                type="button"
                                onClick={onConfigureClick}
                                className={
                                    "p-3 rounded-sm cursor-pointer text-white hover:text-neutral-200 " +
                                    "bg-neutral-500 hover:bg-neutral-600"
                                }
                            >
                                <FontAwesomeSolid
                                    className="p-0! m-0!"
                                    icon="cog"
                                />
                            </button>
                        </>
                    )}
                />
            </ReceiptAccountContext.Provider>
        </ReceiptTypeContext.Provider>
    );
};

export default ReceiptModify;
